import { createColumns, IColumnsGroup, ITableHeader } from "@/utils/ColumnsCreator";
import { CreatedFilters } from "@/services/filters/filters";
import { IFilter } from "@/types";
import { filterClass, SelectFilter } from "@/services/filters/filterClass";
import { computed, ComputedRef } from "vue";
import { getActiveUserSearch, getAffiliateSearch } from "@/api/user";
import { getEmployeesOffersV2 } from "@/api/offers";
import { IOfferSearch } from "@/api/types/offers";
import { IUser } from "@/api/types/user";
import store from "@/store";
import { USER_ROLES } from "@/mappings/user-roles";
import { useWindowResize } from "@/utils/useWindowSize";

export const useMyTasks = (): { headers: ITableHeader[]; headersForTargetDetail: ITableHeader[]; getColumns: () => IColumnsGroup[]; getDefaultColumns: ComputedRef<string[]>; columnsForTargetDetail: string[] } => {
    const headers: ITableHeader[] = [
        { text: 'ID', sortable: false, value: 'id', align: 'center', width: '50px' },
        { text: 'Тип', sortable: false, value: 'target', width: '100px' },
        { text: 'ID Объекта', sortable: false, value: 'target_id', width: '70px' },
        { text: 'Имя объекта', sortable: false, value: 'target_name', width: '150px' },
        { text: 'Автор', sortable: false, value: 'created_by', width: '200px' },
        { text: 'Исполнитель', sortable: false, value: 'assignee', width: '200px' },
        { text: 'Текст задачи', sortable: false, value: 'text', width: 'auto' },
        { text: 'Дедлайн', sortable: false, value: 'date', align: 'right', width: '100px' },
        { text: 'Статус', sortable: false, value: 'status', width: '140px' },
        { text: 'Дата создания', sortable: false, value: 'created_at', align: 'right', width: '100px' },
        { text: 'Дата обновления', sortable: false, value: 'updated_at', align: 'right', width: '100px' },
        { text: 'Дата завершения', sortable: false, value: 'finished_at', align: 'right', width: '100px' },
        { text: '', sortable: false, value: 'actions', fixed: true, align: 'center', width: '55px' },
    ];

    const headersForTargetDetail: ITableHeader[] = [
        { text: 'ID', sortable: false, value: 'id', align: 'center', width: '50px' },
        { text: 'Автор', sortable: false, value: 'created_by', width: '110px' },
        { text: 'Исполнитель', sortable: false, value: 'assignee', width: '110px' },
        { text: 'Текст задачи', sortable: false, value: 'text', width: 'min-content' },
        { text: 'Дедлайн', sortable: false, value: 'date', align: 'right', width: '100px' },
        { text: 'Статус', sortable: false, value: 'status', width: '110px' },
        { text: '', sortable: false, value: 'actions', fixed: true, align: 'center', width: '55px' },
    ];

    const defaultColumns = ['id', 'target', 'target_id', 'target_name', 'created_by', 'assignee', 'text', 'date', 'status'];

    const getDefaultColumns = computed(() => {
        const { width } = useWindowResize();
        return width.value >= 768 ? defaultColumns : defaultColumns.filter(h => h !== 'target');
    });

    const columnsForTargetDetail = ['id', 'created_by', 'assignee', 'text', 'date', 'status'];

    const getColumns = (): IColumnsGroup[] => {
        return createColumns(headers, getDefaultColumns.value);
    };

    return { headers, headersForTargetDetail, getColumns, getDefaultColumns, columnsForTargetDetail };
};

export class TasksFilters extends CreatedFilters {
    constructor() {
        super();
    }

    async changeTargetFiltersOnInit(): Promise<void> {
        const filterTarget = this.filters.find(f => f.id === 'target');
        if (!filterTarget) return;
        filterTarget.callback!(filterTarget);

        const filterTargetId = this.filters.find(f => f.id === 'target_id');
        if (!filterTargetId) return;
        const selected = store.getters['GET_FILTERS_PARAMS_OBJECT'].target_id;

        if (selected === undefined) return;

        const getItems = async (value) => {
            const param = { [filterTargetId.key || 'query']: value };
            if (!filterTargetId.apiMethod) return;
            const res = await filterTargetId.apiMethod(param);
            this.getDataForSelect(filterTargetId, res);
        };

        const ids = Array.isArray(selected) ? selected : [selected];
        for (const id of ids) {
            await getItems(id);
        }

        filterTargetId.select = filterTargetId.items;
        this.updateStartUrl();
    }

    getFilters(): IFilter[] {
        const changeType = (filter?: SelectFilter) => {
            const filterTarget = this.filters.find(f => f.id === 'target_id');

            const defaultTargetFilter = {
                col: 2,
                id: 'target_id',
                label: 'Объект',
                disabled: true,
                select: [],
                items: [],
                tooltip: 'Сначала отфильтруйте задачи по типу',
                multiple: true,
                // сброс конфига предыдущего типа: без этого в ветке «тип снят»
                // apiMethod/key/itemValue/errorText/template остаются от прошлого выбора
                apiMethod: undefined,
                key: undefined,
                itemValue: undefined,
                errorText: undefined,
                template: undefined,
            };

            const offerFilter = {
                errorText: 'Оффер не найден',
                itemValue: 'legacy_id',
                template: (i: IOfferSearch): string => `${i.legacy_id} ${i.name}`,
                apiMethod: getEmployeesOffersV2,
                key: 'search',
            };

            const affiliateFilter = {
                errorText: 'Партнёр не найден',
                itemValue: 'id',
                template: (i: IUser): string => `${i.id} ${i.first_name} ${i.last_name}`,
                apiMethod: getAffiliateSearch,
                key: 'query',
            };

            const employeeFilter = {
                errorText: 'Сотрудник не найден',
                itemValue: 'id',
                template: (i: IUser): string => `${i.id} ${i.first_name} ${i.last_name}`,
                apiMethod: getActiveUserSearch,
                key: 'search',
            };

            if (!filterTarget) return;
            if (filter?.select === 'offer') {
                Object.assign(filterTarget, defaultTargetFilter, offerFilter);
            } else if (filter?.select === 'affiliate') {
                Object.assign(filterTarget, defaultTargetFilter, affiliateFilter);
            } else if (filter?.select === 'employee') {
                Object.assign(filterTarget, defaultTargetFilter, employeeFilter);
            } else {
                Object.assign(filterTarget, defaultTargetFilter);
            }
            filterTarget.select = [];
            filterTarget.items = [];
            filterTarget.disabled = !filter?.select;
        };

        const user = store.getters['authModule/GET_USER'];
        const isAdmin = user?.roles?.includes(USER_ROLES.ADMIN);

        const translateType = (type: 'offer'|'affiliate'|'employee') => {
            switch (type) {
            case 'offer':return 'Оффер';
            case 'affiliate':return 'Партнёр';
            case 'employee':return 'Сотрудник';
            default: return '';
            }
        };

        return [
            new filterClass.Select({
                col: 1,
                id: 'target',
                label: 'Тип',
                items: ['offer', 'affiliate', 'employee'],
                translate: (type) => translateType(type),
                clearable: true,
                callback: changeType,
                remove: changeType,
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'target_id',
                label: 'Объект',
                itemValue: 'id',
                disabled: true,
                select: '',
                tooltip: 'Сначала отфильтруйте задачи по типу',
            }),
            new filterClass.Autocomplete({
                col: 1,
                id: 'author',
                apiMethod: getActiveUserSearch,
                label: 'Автор',
                itemValue: 'id',
                itemText: 'name',
                select: [],
                defaultValue: [],
                items: [],
                isHidden: !isAdmin,
                pinCurrentUser: true,
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'assignee',
                apiMethod: getActiveUserSearch,
                label: 'Исполнитель',
                itemValue: 'id',
                itemText: 'name',
                select: [],
                defaultValue: [],
                items: [],
                isHidden: !isAdmin,
            }),
            new filterClass.Datepicker({
                col: 1,
                id: 'date',
                label: 'Дедлайн',
                select: [],
                pickerType: 'date',
                clearable: true,
            }),
            new filterClass.Multiselect({
                col: 2,
                id: 'status',
                itemValue: 'value',
                itemText: 'text',
                label: 'Статус',
                select: ['to_do', 'expired'],
                items: [
                    { text: 'К выполнению', value: 'to_do' },
                    { text: 'Завершена', value: 'done' },
                    { text: 'Просрочена', value: 'expired' },
                    { text: 'Удалена', value: 'deleted' },
                ],
                template: (i) => i.text,
            }),
        ];
    }
}
